import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type OrganizationId,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { versionConflictCurrent } from '@/lib/version-conflict'
import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { getFeeSettings, updateFeeSettings } from './fee-settings.api'
import type { FeeSettingsInput } from './fee-settings.types'

interface OrganizationScope {
  organizationId: OrganizationIdInput
}

export const feeSettingsQueryKeys = {
  all: ({ organizationId }: OrganizationScope): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'fee-settings' }, { organizationId }),
  ],
  detail: ({ organizationId }: OrganizationScope) =>
    buildScopedAdminQueryKey(feeSettingsQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'detail',
    }),
}

export const feeSettingsQueries = {
  detail: ({ organizationId }: OrganizationScope) =>
    queryOptions({
      queryKey: feeSettingsQueryKeys.detail({ organizationId }),
      queryFn: getFeeSettings,
    }),
}

export function useFeeSettings(organizationId: OrganizationId) {
  return useSuspenseQuery(feeSettingsQueries.detail({ organizationId }))
}

export function useUpdateFeeSettings(organizationId: OrganizationId) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (values: FeeSettingsInput) => updateFeeSettings({ organizationId, values }),
    onError: (error) => {
      const current = versionConflictCurrent(error)
      if (current) client.setQueryData(feeSettingsQueryKeys.detail({ organizationId }), current)
    },
    onSuccess: (data) => client.setQueryData(feeSettingsQueryKeys.detail({ organizationId }), data),
  })
}
